import styled from "styled-components";

// The look of the other SPA admin pages (CardsAdminPage, SchedulerAdmin):
// grey page, white sections, small labelled inputs, plain bordered buttons.
// Phone rules live in GameAdminPage's <style> block under the gadm- prefix.

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  max-width: 1080px;
  width: 100%;
  margin: 0 auto;
  background-color: #f5f5f5;
  min-height: 100vh;
  box-sizing: border-box;
`;

export const Title = styled.h1`
  font-size: 1.5rem;
  color: #222;
  margin-bottom: 0.5rem;
`;

export const Intro = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 1rem;
`;

export const Section = styled.section`
  width: 100%;
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
`;

export const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 12px;
`;

export const SubTitle = styled.h3`
  font-size: 0.95rem;
  color: #333;
  margin: 12px 0 8px;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

export const Tile = styled.div`
  border: 1px solid #ece6f7;
  border-radius: 8px;
  background: #faf7ff;
  padding: 12px;
  min-width: 0;
`;

export const Kicker = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6d5a9a;
  margin-bottom: 6px;
`;

export const Value = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #2e2550;
  margin-bottom: 4px;
  word-break: break-word;
`;

export const Small = styled.div`
  font-size: 0.8rem;
  color: #666;
  word-break: break-word;
`;

export const Row = styled.div.attrs({ className: "gadm-row" })`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
  align-items: flex-end;
`;

export const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;
  color: #555;
  gap: 4px;
  min-width: 140px;
  flex: 1;
`;

export const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.85rem;
  color: #333;
  margin-bottom: 8px;
  input {
    margin-top: 3px;
  }
`;

export const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 0;
  &:disabled {
    background: #f3f3f3;
    color: #777;
  }
`;

export const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  background: #fff;
`;

export const TextArea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 0.9rem;
  min-height: 72px;
  width: 100%;
  box-sizing: border-box;
`;

export const Btn = styled.button<{ variant?: "primary" | "danger" | "ghost" }>`
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid
    ${(p) => (p.variant === "danger" ? "#c62828" : p.variant === "primary" ? "#4f3e76" : "#ccc")};
  background: ${(p) =>
    p.variant === "danger" ? "#ffebee" : p.variant === "primary" ? "#f5f1ff" : "#fff"};
  color: ${(p) => (p.variant === "danger" ? "#c62828" : p.variant === "primary" ? "#4f3e76" : "#333")};
  cursor: pointer;
  font-size: 0.85rem;
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const TableWrap = styled.div.attrs({ className: "gadm-table-wrap" })`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
  th,
  td {
    text-align: left;
    padding: 8px;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
  }
  th {
    color: #666;
    font-weight: 600;
  }
  tr:hover td {
    background: #fafafa;
  }
`;

export const Notice = styled.div<{ tone?: "error" | "ok" | "warn" }>`
  border-radius: 8px;
  padding: 10px 14px;
  margin: 8px 0;
  font-size: 0.88rem;
  border: 1px solid
    ${(p) => (p.tone === "error" ? "#e8a0a0" : p.tone === "ok" ? "#9fd3a8" : "#e8c96a")};
  background: ${(p) => (p.tone === "error" ? "#ffecec" : p.tone === "ok" ? "#eefaf0" : "#fff8e6")};
  color: ${(p) => (p.tone === "error" ? "#7a1f1f" : p.tone === "ok" ? "#1d5a2a" : "#5c4a16")};
`;

export const Pill = styled.span<{ tone?: "ok" | "off" | "warn" | "info" }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) =>
    p.tone === "ok" ? "#d4edda" : p.tone === "warn" ? "#fff3cd" : p.tone === "info" ? "#ede7ff" : "#e9ecef"};
  color: ${(p) =>
    p.tone === "ok" ? "#155724" : p.tone === "warn" ? "#856404" : p.tone === "info" ? "#4f3e76" : "#333"};
`;
